/**
 * Pick mode for "Ask about this" (DNKADM-0463). While on, the page outside the Ask Dink
 * panel is hover-highlighted with a small label of what a click would pick, a click picks
 * it (and is swallowed, so the page never acts on it), and Esc cancels. The listeners are
 * document-level DOM subscriptions, the job an effect is for. Nothing is sent here: the
 * caller gets the described element and fills the question box.
 */
import { Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ASK_DINK_PANEL_ATTR, describeElement, type AskDinkPick } from './askDinkPick';

interface Hover {
  rect: { top: number; left: number; width: number; height: number };
  text: string;
}

const PRESS_EVENTS = ['pointerdown', 'mousedown', 'mouseup'] as const;

export const PICK_UNAVAILABLE_TEXT = "Can't pick this";

export interface AskAboutThis {
  picking: boolean;
  toggle: () => void;
  cancel: () => void;
  /** The highlight outline and label; render once. */
  overlay: ReactNode;
}

function inPanel(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(`[${ASK_DINK_PANEL_ATTR}]`) !== null;
}

/** `enabled` false (panel closed, phone sheet) turns pick mode off whatever was asked. */
export function useAskAboutThis(
  enabled: boolean,
  onPick: (pick: AskDinkPick) => void,
): AskAboutThis {
  const [requested, setRequested] = useState(false);
  const [hover, setHover] = useState<Hover | null>(null);
  // Turning off (panel closed, phone width) drops the request during render, so turning
  // back on never resumes picking.
  if (!enabled && requested) setRequested(false);
  const picking = requested && enabled;
  // The listeners read the latest callback without re-subscribing on every render.
  const onPickRef = useRef(onPick);
  useEffect(() => {
    onPickRef.current = onPick;
  });

  useEffect(() => {
    if (!picking) return;

    function handleMove(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element) || inPanel(target)) {
        setHover(null);
        return;
      }
      const pick = describeElement(target);
      const box = target.getBoundingClientRect();
      setHover({
        rect: { top: box.top, left: box.left, width: box.width, height: box.height },
        text: pick === null ? PICK_UNAVAILABLE_TEXT : `${pick.label} (${pick.role})`,
      });
    }

    // In pick mode the page never sees the press, only the click picks.
    function swallow(event: Event) {
      if (!(event.target instanceof Element) || inPanel(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    }

    function handleClick(event: MouseEvent) {
      const target = event.target;
      if (!(target instanceof Element) || inPanel(target)) return;
      swallow(event);
      const pick = describeElement(target);
      if (pick === null) return;
      setRequested(false);
      setHover(null);
      onPickRef.current(pick);
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      // Esc cancels picking first; it does not also end dictation or close the panel.
      event.stopPropagation();
      setRequested(false);
      setHover(null);
    }

    document.addEventListener('mouseover', handleMove, true);
    document.addEventListener('click', handleClick, true);
    for (const type of PRESS_EVENTS) document.addEventListener(type, swallow, true);
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('mouseover', handleMove, true);
      document.removeEventListener('click', handleClick, true);
      for (const type of PRESS_EVENTS) document.removeEventListener(type, swallow, true);
      document.removeEventListener('keydown', handleKey, true);
    };
  }, [picking]);

  const toggle = useCallback(() => {
    setRequested((prev) => !prev);
    setHover(null);
  }, []);
  const cancel = useCallback(() => {
    setRequested(false);
    setHover(null);
  }, []);

  const overlay =
    picking && hover !== null
      ? createPortal(
          <Box
            aria-hidden
            sx={{
              position: 'fixed',
              top: hover.rect.top,
              left: hover.rect.left,
              width: hover.rect.width,
              height: hover.rect.height,
              outline: '2px solid',
              outlineColor: 'primary.main',
              bgcolor: 'action.hover',
              pointerEvents: 'none',
              zIndex: (theme) => theme.zIndex.tooltip,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                mb: 0.5,
                px: 0.75,
                py: 0.25,
                maxWidth: 280,
                borderRadius: 0.5,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {hover.text}
            </Box>
          </Box>,
          document.body,
        )
      : null;

  return {
    picking,
    toggle,
    cancel,
    overlay,
  };
}
